import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import type { Funnel, FunnelStatus } from 'src/api/generated';
import MultiSelect from 'src/modules/shared/components/atoms/inputs/multi-select';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  GuidedHandoffEmployee,
  GuidedHandoffTemplateKind,
  GuidedHandoffValues,
} from 'src/modules/automations/types/quick-automation.type';

/**
 * Props for the semantic configuration rendered for handoff templates.
 */
interface GuidedHandoffConfigurationProps {
  /** Current funnel loaded from the CRM API. */
  funnel: Funnel | null;
  /** Complete ordered statuses for the current funnel. */
  statuses: FunnelStatus[];
  /** AI Agents that expose a user ID usable by the workflow. */
  employees: GuidedHandoffEmployee[];
  /** Guided variant selected from the template key. */
  kind: GuidedHandoffTemplateKind;
  /** Current form values. */
  values: GuidedHandoffValues;
  /** Whether setup data is still loading. */
  loading: boolean;
  /** Setup request failure, when present. */
  error: Error | null;
  /** Whether the user is allowed to list AI Agents. */
  canReadEmployees: boolean;
  /** Whether controls must remain immutable during creation or retry. */
  disabled: boolean;
  /** Updates the human destination status. */
  onDestinationStatusChange: (statusId: number | null) => void;
  /** Updates the statuses in which the AI Agent is active. */
  onEnabledStatusesChange: (statusIds: number[]) => void;
  /** Updates the selected AI Agent. */
  onEmployeeChange: (employeeId: string | null) => void;
  /** Updates the phrase excluded from handoff. */
  onExcludedContentChange: (content: string) => void;
  /** Retries all guided setup requests. */
  onRetry: () => void;
}

/**
 * Renders domain controls instead of exposing raw trigger mappings.
 */
export const GuidedHandoffConfiguration: React.FC<
  GuidedHandoffConfigurationProps
> = ({
  funnel,
  statuses,
  employees,
  kind,
  values,
  loading,
  error,
  canReadEmployees,
  disabled,
  onDestinationStatusChange,
  onEnabledStatusesChange,
  onEmployeeChange,
  onExcludedContentChange,
  onRetry,
}) => {
  const { t } = useI18n();

  if (!canReadEmployees) {
    return (
      <ErrorState
        size="sm"
        appearance="soft"
        title={t('automations.quickHandoff.errors.employeePermissionTitle')}
        description={t(
          'automations.quickHandoff.errors.employeePermissionDescription'
        )}
      />
    );
  }

  if (error) {
    return (
      <ErrorState
        size="sm"
        appearance="soft"
        title={t('automations.quickHandoff.errors.setupTitle')}
        description={t('automations.quickHandoff.errors.setupDescription')}
        actionContent={t('automations.quickHandoff.actions.retryLoad')}
        onAction={onRetry}
      />
    );
  }

  const destinationOptions = statuses.map((status) => ({
    value: String(status.id),
    textValue: status.name,
    content: (
      <FunnelStatusName name={status.name} outcome={status.terminalOutcome} />
    ),
  }));
  const enabledStatusOptions = statuses
    .filter((status) => status.id !== values.destinationStatusId)
    .map((status) => ({
      value: String(status.id),
      textValue: status.name,
      content: (
        <FunnelStatusName name={status.name} outcome={status.terminalOutcome} />
      ),
    }));
  const employeeOptions = employees.map((employee) => ({
    value: employee.employeeId,
    content: employee.userDisplayName,
  }));

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-primary-100 bg-primary-50 p-3">
        <Text size="tiny" className="text-primary-700">
          {t('automations.quickHandoff.fields.funnel')}
        </Text>
        <Text size="small" weight="semibold" className="text-primary-950">
          {funnel?.name ??
            t('automations.quickHandoff.fields.currentFunnelFallback')}
        </Text>
      </div>

      <label className="block space-y-1">
        <Text as="span" size="tiny" weight="medium">
          {t('automations.quickHandoff.fields.destinationStatus')}
        </Text>
        <SearchableSelect
          value={
            values.destinationStatusId === null
              ? undefined
              : String(values.destinationStatusId)
          }
          options={destinationOptions}
          onChange={(value) =>
            onDestinationStatusChange(value ? Number(value) : null)
          }
          loading={loading}
          disabled={disabled}
          placeholder={t(
            'automations.quickHandoff.placeholders.destinationStatus'
          )}
          emptyLabel={t('automations.quickHandoff.empty.statuses')}
        />
      </label>

      <label className="block space-y-1">
        <Text as="span" size="tiny" weight="medium">
          {t('automations.quickHandoff.fields.enabledStatuses')}
        </Text>
        <MultiSelect
          value={values.enabledStatusIds.map(String)}
          options={enabledStatusOptions}
          onChange={(statusIds) =>
            onEnabledStatusesChange(statusIds.map(Number))
          }
          loading={loading}
          disabled={disabled}
          placeholder={t(
            'automations.quickHandoff.placeholders.enabledStatuses'
          )}
        />
        {!loading && values.enabledStatusIds.length === 0 && (
          <Text size="tiny" className="text-warning-700">
            {t('automations.quickHandoff.hints.noSuggestedStatuses')}
          </Text>
        )}
      </label>

      <label className="block space-y-1">
        <Text as="span" size="tiny" weight="medium">
          {t('automations.quickHandoff.fields.aiAgent')}
        </Text>
        <SearchableSelect
          value={values.employeeId ?? undefined}
          options={employeeOptions}
          onChange={(value) => onEmployeeChange(value ?? null)}
          loading={loading}
          disabled={disabled}
          placeholder={t('automations.quickHandoff.placeholders.aiAgent')}
          emptyLabel={t('automations.quickHandoff.empty.aiAgents')}
        />
      </label>

      {kind === 'except-content' && (
        <label className="block space-y-1">
          <Text as="span" size="tiny" weight="medium">
            {t('automations.quickHandoff.fields.excludedContent')}
          </Text>
          <TextInput
            value={values.excludedMessageContent}
            onChange={(event) => onExcludedContentChange(event.target.value)}
            disabled={disabled || loading}
            placeholder={t(
              'automations.quickHandoff.placeholders.excludedContent'
            )}
          />
          <Text size="tiny" className="text-secondary-500">
            {t('automations.quickHandoff.hints.excludedContent')}
          </Text>
        </label>
      )}
    </div>
  );
};
